import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { FacilityCodeGroup } from './FacilityCodeGroup'
import { FacilityID } from './FacilityID'
import type { SearchProperty } from '../../property/infrastructure/HTTPPropertyRepository/schemas/v3/search/legacy'
import { Name } from '../../_kernel/Name'

export const FacilityValidation = z.object({
  codeGroup: z.instanceof(FacilityCodeGroup, {
    message: '[Facility] codeGroup of items is required',
  }),
  id: z.instanceof(FacilityID, { message: '[Facility] id is required' }),
  name: z.instanceof(Name, { message: '[Facility] name is required' }).optional(),
})

export class Facility extends Model {
  static create({ codeGroup, id, name }: z.infer<typeof FacilityValidation>) {
    FacilityValidation.parse({ codeGroup, id, name })
    return new Facility(codeGroup, id, name)
  }

  static fromJSON({ json }: { json: ReturnType<Facility['toJSON']> }) {
    return Facility.create({
      codeGroup: FacilityCodeGroup.create({ value: json.codeGroup }),
      id: FacilityID.create({ value: json.id }),
      ...(json.name ? { name: Name.create({ value: json.name }) } : {}),
    })
  }

  static fromPrimitive({ codeGroup, id, name }: { codeGroup: string; id: string; name?: string }) {
    return Facility.create({
      codeGroup: FacilityCodeGroup.create({ value: codeGroup }),
      id: FacilityID.create({ value: id }),
      ...(name ? { name: Name.create({ value: name }) } : {}),
    })
  }

  static fromSerpApiLegacy({ id }: { id: SearchProperty['facilities'][number]['name'] }) {
    return Facility.create({
      codeGroup: FacilityCodeGroup.create({ value: '' }),
      id: FacilityID.create({ value: id }),
    })
  }

  constructor(
    private readonly _codeGroup: FacilityCodeGroup,
    private readonly _id: FacilityID,
    private readonly _name?: Name
  ) {
    super()
  }

  get codeGroup() { return this._codeGroup.value } // prettier-ignore
  get id() { return this._id.value } // prettier-ignore

  name() { return this._name?.value ?? '' } // prettier-ignore

  toJSON() {
    return {
      codeGroup: this.codeGroup,
      id: this.id,
      ...(this._name && { name: this._name.value }),
    }
  }

  isCategory(category: FacilityCodeGroup) {
    return this._codeGroup.value === category.value
  }
}
